import { useEffect, useState } from 'react';
import {
  type UseQueryOptions,
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { createApiClient } from '@/api/api-client';
import type {
  SmartyAutocompleteProQueryParams,
  SmartyAutocompleteProResponse,
} from '@/api/schemas/smarty-api.schema';

const smartyApi = createApiClient('https://us-autocomplete-pro.api.smarty.com');

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_INPUT_QUERY_LENGTH = 3;

type GetAddressAutocompleteQueryOptions = Omit<
  UseQueryOptions<SmartyAutocompleteProResponse>,
  'queryKey' | 'queryFn' | 'select' | 'enabled'
>;

const addressAutocompleteQueryKeys = {
  all: ['address-autocomplete'] as const,
  byQuery: (query: string) => [...addressAutocompleteQueryKeys.all, query] as const,
} as const;

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

async function fetchAddressAutocomplete(
  params: Omit<SmartyAutocompleteProQueryParams, 'key'>,
  signal?: AbortSignal
): Promise<SmartyAutocompleteProResponse> {
  return smartyApi.get<SmartyAutocompleteProResponse>('/lookup', {
    params: {
      key: import.meta.env.VITE_PUBLIC_SMARTY_API_KEY,
      ...params,
    },
    signal,
  });
}

function useGetAddressAutocompleteQuery(
  params: Omit<SmartyAutocompleteProQueryParams, 'key'>,
  options?: GetAddressAutocompleteQueryOptions & {
    debounceMs?: number;
    minInputQueryLength?: number;
    enabled?: boolean;
  }
) {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minInputQueryLength = DEFAULT_MIN_INPUT_QUERY_LENGTH,
    enabled = true,
    ...queryOptions
  } = options ?? {};

  const queryClient = useQueryClient();

  // Check if the immediate (non-debounced) query is already cached
  const immediateQuery = params.search.trim();
  const immediateQueryKey = addressAutocompleteQueryKeys.byQuery(immediateQuery);
  const isCached = queryClient.getQueryData(immediateQueryKey) !== undefined;

  // Only debounce if the query is not already cached
  const debouncedQuery = useDebounce(params.search, debounceMs);

  // Use immediate query if cached, otherwise use debounced query
  const sanitizedQuery = isCached ? immediateQuery : debouncedQuery.trim();

  const isEnabled = enabled && sanitizedQuery.length >= minInputQueryLength;

  const queryResult = useQuery({
    queryKey: addressAutocompleteQueryKeys.byQuery(sanitizedQuery),
    queryFn: async ({ signal }) =>
      fetchAddressAutocomplete(
        {
          ...params,
          search: sanitizedQuery,
        },
        signal
      ),
    select: (response) => response.suggestions ?? [],
    enabled: isEnabled,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });

  return queryResult;
}

export {
  addressAutocompleteQueryKeys,
  useGetAddressAutocompleteQuery,
  DEFAULT_DEBOUNCE_MS,
  DEFAULT_MIN_INPUT_QUERY_LENGTH,
};
